import type { SuggestionUnit } from './EditorAlert/suggestion-units';
import { createPublishedTabScopedRegistry } from './tab-scoped-registry';

export interface EditorSuggestionState {
  handleAllAiEdits: boolean;
  recomputeSuggestions: () => void;
  selectedSuggestionGroupId: string | null;
  setHandleAllAiEdits: (value: boolean) => void;
  setSelectedSuggestionGroupId: (suggestionId: string | null) => void;
  suggestionUnits: SuggestionUnit[];
}

/**
 * AI suggestion state (paragraph edit groups, the selected group index, and the
 * handle-all toggle) is per-document/per-tab, not global. Frozen tabs would
 * otherwise share a single Apollo reactive var and overwrite each other on tab
 * switches, leaving the toolbar visible on the wrong document.
 *
 * This registry mirrors editor-runtime-registry and editor-ui-state-registry:
 * `EditorSuggestionStateProvider` writes from inside the editor subtree, and
 * `EditorSuggestionStateScope` subscribes from outside (e.g. the right drawer's
 * AI chat panel) for the active tab. Both expose the same context, so the read
 * hook works uniformly inside and outside the tab tree.
 */
const EMPTY_STATE: EditorSuggestionState = {
  handleAllAiEdits: false,
  recomputeSuggestions: () => {},
  selectedSuggestionGroupId: null,
  setHandleAllAiEdits: () => {},
  setSelectedSuggestionGroupId: () => {},
  suggestionUnits: [],
};

const editorSuggestionStateRegistry = createPublishedTabScopedRegistry<EditorSuggestionState>({
  useTabIdErrorMessage:
    'useEditorSuggestionState must be used within an editor suggestion state scope.',
});

export const EditorSuggestionStateProvider = editorSuggestionStateRegistry.Provider;
export const EditorSuggestionStateScope = editorSuggestionStateRegistry.Scope;

export function useMaybeEditorSuggestionState(): EditorSuggestionState | null {
  return editorSuggestionStateRegistry.useMaybeState();
}

/**
 * Single source of truth for "is the suggestions toolbar showing?". Used by
 * both `EditorAlert` (which renders the toolbar) and any sibling that needs
 * to lay out around it (e.g. the outline toggle). The toolbar appears when
 * the document has any suggestion unit — a sourced-edit group (with or
 * without a marker pair) or a legacy marker-only suggestion — so consumers
 * that care about layout must match this exact rule.
 */
export function getIsSuggestionsAlertVisible(
  state: Pick<EditorSuggestionState, 'suggestionUnits'>
): boolean {
  return state.suggestionUnits.length > 0;
}

/**
 * Reads suggestion state for the surrounding tab. Falls back to an empty state
 * when no provider/scope is in scope (e.g. chat panel rendered with no active
 * tab) so callers do not need to null-check.
 */
export function useEditorSuggestionState(): EditorSuggestionState {
  return useMaybeEditorSuggestionState() ?? EMPTY_STATE;
}
